// Sidebar configuration. Every entry is a NATIVE in-app route — the console is
// the whole tool. (The last external links — Product Config Editor /
// Multi-Logo Designs / Pricing Rules → product-config-admin, Create Store →
// admin-dashboard, Bulk Import → productconfig-dashboard — were replaced by
// native pages in the 2026-06-10 "no click-aways" pass.)
import type { LucideIcon } from 'lucide-react';
import {
  House, Inbox, FileClock, BadgeCheck,
  PackageCheck,
  ShoppingCart, Truck, Repeat2, FlaskConical,
  Store, CirclePlus, BarChart3,
  PenTool, Eye, Layers, LibraryBig,
  Type, Link2,
  Shirt, Frame, Gift, CircleDollarSign, Banknote, CloudUpload, Database, Images, Package,
  MessageSquare,
  GraduationCap, SquareKanban, Send,
  Activity, Bot,
  WandSparkles, Settings2,
} from 'lucide-react';

export type NavLink = { label: string; to: string; icon: LucideIcon };

export type NavGroup = {
  label: string;
  items: NavLink[];
};

export const NAV: NavGroup[] = [
  {
    // One queue per channel: prize fundraisers (submissions collection) and
    // team stores (teamStores collection). The old separate "Worker Review"
    // pages were merged into these on 2026-06-10 — same action drawers.
    label: 'Operations',
    items: [
      { label: 'Today',                   to: '/',                       icon: House },
      { label: 'Prize Submissions',      to: '/submissions',            icon: Inbox },
      { label: 'Team Store Submissions', to: '/team-store-submissions', icon: PackageCheck },
      { label: 'Revisions',              to: '/revisions',              icon: FileClock },
      { label: 'Pending Approvals',      to: '/pending-approvals',      icon: BadgeCheck },
    ],
  },
  {
    label: 'Ordering',
    items: [
      { label: 'Place Orders',   to: '/ordering', icon: ShoppingCart },
      { label: 'Order Tracking', to: '/orders',   icon: Truck },
      { label: 'Reorder',        to: '/reorder',  icon: Repeat2 },
      { label: 'Samples',        to: '/samples',  icon: FlaskConical },
    ],
  },
  {
    label: 'Team Stores',
    items: [
      { label: 'Active Stores', to: '/active-stores', icon: Store },
      { label: 'Create Store',  to: '/create-store',  icon: CirclePlus },
      { label: 'Payouts',       to: '/payouts',       icon: Banknote },
      { label: 'Chat',          to: '/chat',          icon: MessageSquare },
      // No Rollouts entry: fleet /rollout + scheduled /drops live in the
      // unmerged fleet PR (#355). Add the nav item with the proxy, not before.
    ],
  },
  {
    // The design-template workflow, in the order a rep actually uses it:
    // 1) create a design  2) preview/proof it  3) curate the product lineup
    // 4) assign designs onto garments  5) browse the library.
    label: 'Design Templates',
    items: [
      { label: 'New Design',         to: '/template-author',         icon: PenTool },
      { label: 'Preview / Proof',    to: '/design-studio',           icon: Eye },
      { label: 'Store Builder',      to: '/store-builder',           icon: Shirt },
      { label: 'Product lineups',    to: '/product-template-editor', icon: Package },
      { label: 'Assign to Products', to: '/store-template-editor',   icon: Layers },
      { label: 'Library',            to: '/templates',               icon: LibraryBig },
    ],
  },
  {
    // Customer personalization (name/number on garments): manage the template
    // catalog + the storefront V2 switch, then attach templates to products.
    label: 'Personalization',
    items: [
      { label: 'Templates',          to: '/personalization/templates', icon: Type },
      { label: 'Product Assignment', to: '/personalization/assign',    icon: Link2 },
    ],
  },
  {
    label: 'Reports',
    items: [
      { label: 'Reports',                 to: '/reports',                  icon: BarChart3 },
      { label: 'Payouts',                 to: '/payouts',                  icon: Banknote },
      { label: 'Personalization Billing', to: '/personalization/billing',  icon: CircleDollarSign },
    ],
  },
  {
    label: 'Catalog',
    items: [
      { label: 'Products',          to: '/products',       icon: Shirt },
      { label: 'Multi-Logo Designs',to: '/multi-logo',     icon: Frame },
      { label: 'Prize Products',    to: '/prize-products', icon: Gift },
      { label: 'Pricing Rules',     to: '/pricing',        icon: CircleDollarSign },
      { label: 'Player Packs',      to: '/player-packs',   icon: Package },
      { label: 'Bulk Import',       to: '/bulk-import',    icon: CloudUpload },
      { label: 'Supplier Catalog',  to: '/supplier-catalog', icon: Database },
      { label: 'Logo Library',      to: '/logos',          icon: Images },
    ],
  },
  {
    label: 'Scout',
    items: [
      { label: 'Schools',  to: '/schools',  icon: GraduationCap },
      { label: 'Pipeline', to: '/pipeline', icon: SquareKanban },
      { label: 'Outreach', to: '/outreach', icon: Send },
    ],
  },
  {
    // Read-only system health (errors, webhooks, reconciliation, inventory
    // forecast) — ported from admin-dashboard's ops.html.
    label: 'System',
    items: [
      { label: 'Ops', to: '/ops', icon: Activity },
      { label: 'Agent', to: '/agent', icon: Bot },
    ],
  },
  {
    label: 'Settings',
    items: [
      { label: 'Recipes',       to: '/recipes',       icon: WandSparkles },
      { label: 'Configuration', to: '/configuration', icon: Settings2 },
    ],
  },
];

// Reverse lookup for the top bar: route path -> {group, label}.
export function findNavEntry(pathname: string): { group: string; label: string } | null {
  if (pathname === '/store' || pathname.startsWith('/store/')) {
    return { group: 'Team Stores', label: 'Active Stores' };
  }
  for (const group of NAV) {
    for (const item of group.items) {
      if (item.to === pathname || (item.to !== '/' && pathname.startsWith(`${item.to}/`))) {
        return { group: group.label, label: item.label };
      }
    }
  }
  return null;
}
